/**
 * Sketchy (hand-drawn) SVG path generation for diagram edges.
 *
 * Takes the crisp edge path that ElkEdge already computes (either the ELK
 * bend-point polyline or the smooth-step fallback) and re-draws it with the
 * rough.js generator. Working from the path *string* keeps the caller's
 * memoization keyed on primitives: the string fully serializes the geometry,
 * so identical geometry + seed always yields the identical sketch.
 *
 * Output is deterministic for a given seed (see seed.ts), so repeated
 * renders and metric-driven style updates never make edges jitter.
 */
import rough from "roughjs";
import type { RoughGenerator } from "roughjs/bin/generator";
import type { Options } from "roughjs/bin/core";

// Matches the node sketchiness (see node-paths.ts) so edges and shapes read
// as drawn by the same hand.
const ROUGHNESS = 1.5;

// The generator is stateless across calls when a seed is supplied, so one
// shared instance is safe.
const generator: RoughGenerator = rough.generator();

/**
 * Parse a path of the exact form produced by ElkEdge's bendPointsToPath
 * ("M x,y L x,y ..."). Returns null for anything else (e.g. the curved
 * smooth-step fallback), signalling the caller to use the generic path
 * renderer instead of linearPath.
 */
function parsePolylinePoints(d: string): [number, number][] | null {
  const tokens = d.split(" ");
  if (tokens.length < 4 || tokens.length % 2 !== 0) return null;
  const points: [number, number][] = [];
  for (let i = 0; i < tokens.length; i += 2) {
    const expected = i === 0 ? "M" : "L";
    if (tokens[i] !== expected) return null;
    const coords = tokens[i + 1].split(",");
    if (coords.length !== 2) return null;
    const x = Number(coords[0]);
    const y = Number(coords[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    points.push([x, y]);
  }
  return points;
}

/**
 * Generate the sketchy stroke path data (`d`) for an edge.
 *
 * `crispPath` is the edge geometry as an SVG path string; bend-point
 * polylines go through rough's linearPath (segment-by-segment overshoot),
 * anything else through the generic path renderer. Stroke color, width and
 * dash are intentionally not baked in — they stay CSS/props concerns on the
 * rendered <path>, exactly like the crisp edge.
 */
export function sketchEdgeD(crispPath: string, seed: number): string {
  const opts: Options = {
    seed,
    roughness: ROUGHNESS,
    // Edges read as one continuous squiggly line: a single stroke pass
    // (nodes keep rough's default double pass) with stronger bowing so
    // segments curve instead of stacking overlapping straight strokes.
    disableMultiStroke: true,
    bowing: 2,
  };
  const points = parsePolylinePoints(crispPath);
  const drawable =
    points !== null
      ? generator.linearPath(points, opts)
      : generator.path(crispPath, opts);
  return generator
    .toPaths(drawable)
    .map((p) => p.d)
    .join(" ");
}
